import React, { useEffect, useState, useRef } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { Sidebar } from "primereact/sidebar";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { AutoComplete } from "primereact/autocomplete";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { Skeleton } from "primereact/skeleton";
import useAccessToken from "../Custom/useAccessToken";
import {
  getProfessionalExperienceOutsideLBMC,
  getSkills,
  getEmployeeType,
} from "../Services/GetService";
import { deleteProfessionalExperience } from "../Services/DeleteService";
import { saveProfessionalExperience } from "../Services/ProfesionalExperienceService";
import { saveSkill } from "../Services/SkillService";
import { formatDateUsingMoment } from "../Utils/Year";

interface ExperienceSkill {
  skillId: string;
  skillName: string;
}

interface EmploymentType {
  employmentTypeId: string; // UUID
  employmentTypeName: string;
}

interface ProfileCardDashboardProps {
  profileID?: string | null;
  isOwnProfile?: boolean;
}

interface OutsideExp {
  professionalWorkExperienceId: string;
  jobTitle: string;
  company: string;
  employmentTypeId: string;
  employmentTypeName: string;
  companyWebsite: string;
  location: string;
  startDate: string; // ISO string
  endDate: string; // ISO string or null if current
  jobDetails: string;
  skillsList: ExperienceSkill[];
}

const months = [
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];
const years = Array.from({ length: 50 }, (_, i) => ({
  label: `${2025 - i}`,
  value: 2025 - i,
}));

const ProfessionalExperienceOutsideLBMC: React.FC<
  ProfileCardDashboardProps
> = ({ profileID, isOwnProfile }) => {
  const { accessToken } = useAccessToken();
  const [skillsOptions, setSkillsOptions] = useState<ExperienceSkill[]>([]);
  const [employmentTypes, setEmploymentTypes] = useState<EmploymentType[]>([]);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const toast = useRef<any>(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [experiences, setExperiences] = useState<OutsideExp[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    jobTitle: "",
    company: "",
    companyWebsite: "",
    employmentType: null as string | null,
    location: "",
    startMonth: null as string | null,
    startYear: null as number | null,
    endMonth: null as string | null,
    endYear: null as number | null,
    current: false,
    description: "",
    skills: [] as string[],
  });

  const [selectedSkills, setSelectedSkills] = useState<ExperienceSkill[]>([]);
  const [skillInput, setSkillInput] = useState("");
  const [filteredSkills, setFilteredSkills] = useState<ExperienceSkill[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [editExpId, setEditExpId] = useState<string | null>(null);
  const [dateErrors, setDateErrors] = useState({
    startDate: "",
    endDate: "",
  });

  // Get employment types for Dropdown
  useEffect(() => {
    const fetchEmploymentTypes = async () => {
      if (accessToken) {
        try {
          const types = await getEmployeeType(accessToken);
          // Fix: Ensure the mapping creates the correct structure
          const formattedTypes = types.map((type: any) => ({
            label: type.employmentTypeName,
            value: type.employmentTypeId,
          }));
          setEmploymentTypes(formattedTypes);
        } catch (error) {
          console.error("Failed to fetch employment types:", error);
          setEmploymentTypes([]);
        }
      } else {
        setEmploymentTypes([]);
      }
    };
    fetchEmploymentTypes();
  }, [accessToken]);

  // Fetch skills for MultiSelect
  useEffect(() => {
    const fetchSkills = async () => {
      if (accessToken) {
        try {
          const skills = await getSkills(accessToken);
          setSkillsOptions(skills);
        } catch {
          setSkillsOptions([]);
        }
      } else {
        setSkillsOptions([]);
      }
    };
    fetchSkills();
  }, [accessToken]);

  useEffect(() => {
    if (sidebarVisible) return; // Skip fetching when sidebar is open
    const fetchProfessionalExperienceOutsideLBMC = async () => {
      if (!accessToken || !profileID) return;

      setLoading(true);
      try {
        const token = accessToken;
        const experience: OutsideExp[] =
          await getProfessionalExperienceOutsideLBMC(token, profileID);
        setExperiences(experience);
      } catch (error) {
        console.error("Failed to fetch professional experience", error);
        setExperiences([]);
      }
      setLoading(false);
    };

    fetchProfessionalExperienceOutsideLBMC();
  }, [accessToken, sidebarVisible, profileID]);

  // Helper functions for skills autocomplete
  const searchSkills = (event: { query: string }) => {
    const filtered = skillsOptions.filter((skill) =>
      skill.skillName.toLowerCase().includes(event.query.toLowerCase())
    );
    setFilteredSkills(filtered);
  };

  const emptyFormData = () => {
    // Clear your form data here if needed
    setSidebarVisible(false);

    setSidebarVisible(false);
    setEditMode(false);
    setEditExpId(null);
    setForm({
      jobTitle: "",
      company: "",
      companyWebsite: "",
      employmentType: null,
      location: "",
      startMonth: null,
      startYear: null,
      endMonth: null,
      endYear: null,
      current: false,
      description: "",
      skills: [],
    });
    setSelectedSkills([]);
    setSkillInput("");
    setDateErrors({
      startDate: "",
      endDate: "",
    });
  };

  const handleSkillSelect = async (e: { value: string }) => {
    await addSkill(e.value);
  };

  const handleSkillKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const overlay = document.querySelector(".p-autocomplete-panel");

      const highlighted = overlay?.querySelector(".p-highlight");

      if (highlighted) {
        return;
      }

      if (skillInput.trim()) {
        e.preventDefault();
        await addSkill(skillInput.trim());
      }
    }
  };


  const addSkill = async (skillName: string) => {
    if (!skillName) return;

    // Check if it's an existing skill
    const existingSkill = skillsOptions.find(
      (skill) => skill.skillName.toLowerCase() === skillName.toLowerCase()
    );

    if (existingSkill) {
      // Check if skill is already selected
      if (
        selectedSkills.find((skill) => skill.skillId === existingSkill.skillId)
      ) {
        toast.current?.show({
          severity: "info",
          summary: "Already Added",
          detail: `Skill "${skillName}" is already selected`,
          life: 2000,
        });
        setSkillInput("");
        return;
      }

      // Add existing skill
      const newSelectedSkills = [...selectedSkills, existingSkill];
      setSelectedSkills(newSelectedSkills);
      handleChange(
        "skills",
        newSelectedSkills.map((skill) => skill.skillId)
      );
    } else {
      // Check if we're trying to add a skill that matches an already selected skill by name
      if (
        selectedSkills.find(
          (skill) => skill.skillName.toLowerCase() === skillName.toLowerCase()
        )
      ) {
        toast.current?.show({
          severity: "info",
          summary: "Already Added",
          detail: `Skill "${skillName}" is already selected`,
          life: 2000,
        });
        setSkillInput("");
        return;
      }

      // Create new skill
      try {
        if (accessToken) {
          const newSkillData = {
            skillId: null,
            skillName: skillName,
          };

          const savedSkill = await saveSkill(accessToken, newSkillData);

          // The SaveSkill API returns the new skillId as a string (GUID)
          const newSkillId = savedSkill;

          if (
            !newSkillId ||
            typeof newSkillId !== "string" ||
            newSkillId.trim() === ""
          ) {
            console.error(
              "SaveSkill API returned invalid skillId:",
              savedSkill
            );
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: "Failed to create skill - API returned invalid response",
              life: 3000,
            });
            setSkillInput("");
            return;
          }

          // Add the new skill to the options and selected skills
          const newSkill: ExperienceSkill = {
            skillId: newSkillId,
            skillName: skillName,
          };

          setSkillsOptions((prev) => [...prev, newSkill]);
          const newSelectedSkills = [...selectedSkills, newSkill];
          setSelectedSkills(newSelectedSkills);
          handleChange(
            "skills",
            newSelectedSkills.map((skill) => skill.skillId)
          );

          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: `New skill "${skillName}" created and added`,
            life: 3000,
          });
        }
      } catch (error) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to create new skill",
          life: 3000,
        });
        console.error("Failed to create new skill", error);
      }
    }

    setSkillInput("");
  };

  const removeSkill = (skillToRemove: ExperienceSkill) => {
    const newSelectedSkills = selectedSkills.filter(
      (skill) => skill.skillId !== skillToRemove.skillId
    );
    setSelectedSkills(newSelectedSkills);
    handleChange(
      "skills",
      newSelectedSkills.map((skill) => skill.skillId)
    );
  };

  // URL validation helper function — use URL constructor for robust checks.
  const isValidURL = (string: string): boolean => {
    if (!string.trim()) return true; // Empty URL is valid (optional field)

    // Try the URL constructor first. If it fails and the user omitted protocol,
    // attempt to prepend https:// and try again.
    try {
      const url = new URL(string);
      return (
        (url.protocol === "http:" || url.protocol === "https:") &&
        url.hostname.length > 0 &&
        url.hostname.includes(".")
      );
    } catch {
      try {
        const url = new URL(`https://${string}`);
        return url.hostname.length > 0 && url.hostname.includes(".");
      } catch {
        return false;
      }
    }
  };

  // Date validation helper functions
  const validateDates = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11

    const errors = {
      startDate: "",
      endDate: "",
    };

    if ((form.startMonth && !form.startYear) || (!form.startMonth && form.startYear)) {
      errors.startDate = "Please select both the start month and year, or leave both fields blank";
    }

    // Validate start date
    if (form.startMonth && form.startYear) {
      const startDate = new Date(
        form.startYear,
        parseInt(form.startMonth) - 1,
        1
      );
      const currentDateFirstDay = new Date(currentYear, currentMonth - 1, 1);

      if (startDate > currentDateFirstDay) {
        errors.startDate = "Start date cannot be in the future";
      }
    }

    if (((form.endMonth && !form.endYear) || (!form.endMonth && form.endYear))) {
      errors.endDate = "Please select both the end month and year, or leave both fields blank";
    }

    // Validate end date (only if not current role)
    if (!form.current && form.endMonth && form.endYear) {
      const endDate = new Date(form.endYear, parseInt(form.endMonth) - 1, 1);
      const currentDateFirstDay = new Date(currentYear, currentMonth - 1, 1);

      if (endDate > currentDateFirstDay) {
        errors.endDate = "End date cannot be in the future";
      }

      // Check if end date is before start date
      if (form.startMonth && form.startYear) {
        const startDate = new Date(
          form.startYear,
          parseInt(form.startMonth) - 1,
          1
        );
        if (endDate < startDate) {
          errors.endDate = "End date cannot be before start date";
        }
      }
    }

    setDateErrors(errors);
    return !errors.startDate && !errors.endDate;
  };

  const handleSubmitEducation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessToken) return;

    // Basic required field validation
    if (
      !form.jobTitle ||
      !form.company ||
      !form.employmentType ||
      !form.location 
      // !form.startMonth || !form.startYear ||
      // (form.current === false && (!form.endMonth || !form.endYear))
    ) {
      toast.current?.show({
        severity: "error",
        summary: "Missing required fields.",
        detail: "Please complete all required fields.",
        life: 3000,
      });
      return;
    }

    // URL validation
    if (form.companyWebsite && !isValidURL(form.companyWebsite)) {
      toast.current?.show({
        severity: "error",
        summary: "Validation Error",
        detail: "Please enter a valid URL (e.g., https://example.com)",
        life: 4000,
      });
      return;
    }

    // Date validation
    if (!validateDates()) {
      toast.current?.show({
        severity: "error",
        summary: "Date Validation Error",
        detail: "Please check the date fields for errors",
        life: 4000,
      });
      return;
    }

    const experienceData = {
      professionalWorkExperienceId: editExpId,
      jobTitle: form.jobTitle,
      company: form.company,
      companyWebsite: form.companyWebsite,
      employmentTypeId: form.employmentType,
      location: form.location,
      startDate: form.startMonth && form.startYear ? `${form.startYear}-${form.startMonth}-01` : null,
      endDate:
        form.current === false ? (form.endMonth && form.endYear) ? `${form.endYear}-${form.endMonth}-01` : null : null,
      jobDetails: form.description,
      skillsList: form.skills,
    };
    try {
      await saveProfessionalExperience(accessToken, experienceData);
      if (editMode) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Experience updated successfully",
          life: 3000,
        });
      } else {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Experience added successfully",
          life: 3000,
        });
      }
      setSidebarVisible(false);
      setEditMode(false);
      setEditExpId(null);
      setForm({
        jobTitle: "",
        company: "",
        companyWebsite: "",
        employmentType: null,
        location: "",
        startMonth: null,
        startYear: null,
        endMonth: null,
        endYear: null,
        current: false,
        description: "",
        skills: [],
      });
      setSelectedSkills([]);
      setSkillInput("");
      // Clear date errors when form is reset
      setDateErrors({
        startDate: "",
        endDate: "",
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save experience",
        life: 3000,
      });
      console.error("Failed to save experience", error);
    }
  };

  const customHeader = (
    <div className="flex align-items-center gap-2 px-2">
      <span className="font-medium text-900 text-lg">
        {editMode
          ? "Edit Experience Outside LBMC"
          : "Add Experience Outside LBMC"}
      </span>
    </div>
  );

  const handleChange = (
    field: string,
    value: string | number | boolean | string[] | null
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));

    // Trigger date validation when date fields change
    if (
      ["startMonth", "startYear", "endMonth", "endYear", "current"].includes(
        field
      )
    ) {
      // Use setTimeout to ensure state is updated before validation
      setTimeout(() => {
        const updatedForm = { ...form, [field]: value };
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;

        const errors = {
          startDate: "",
          endDate: "",
        };

        if ((updatedForm.startMonth && !updatedForm.startYear) || (!updatedForm.startMonth && updatedForm.startYear)) {
          errors.startDate = "Please select both the start month and year, or leave both fields blank";
        }

        // Validate start date
        if (updatedForm.startMonth && updatedForm.startYear) {
          const startDate = new Date(
            updatedForm.startYear,
            parseInt(updatedForm.startMonth) - 1,
            1
          );
          const currentDateFirstDay = new Date(
            currentYear,
            currentMonth - 1,
            1
          );

          if (startDate > currentDateFirstDay) {
            errors.startDate = "Start date cannot be in the future";
          }
        }

        if ( ((updatedForm.endMonth && !updatedForm.endYear) || (!updatedForm.endMonth && updatedForm.endYear))) {
          errors.endDate = "Please select both the end month and year, or leave both fields blank";
        }

        // Validate end date (only if not current role)
        if (
          !updatedForm.current &&
          updatedForm.endMonth &&
          updatedForm.endYear
        ) {
          const endDate = new Date(
            updatedForm.endYear,
            parseInt(updatedForm.endMonth) - 1,
            1
          );
          const currentDateFirstDay = new Date(
            currentYear,
            currentMonth - 1,
            1
          );

          if (endDate > currentDateFirstDay) {
            errors.endDate = "End date cannot be in the future";
          }

          // Check if end date is before start date
          if (updatedForm.startMonth && updatedForm.startYear) {
            const startDate = new Date(
              updatedForm.startYear,
              parseInt(updatedForm.startMonth) - 1,
              1
            );
            if (endDate < startDate) {
              errors.endDate = "End date cannot be before start date";
            }
          }
        }

        setDateErrors(errors);
      }, 0);
    }
  };

  const handleEditExperience = (exp: OutsideExp) => {
    setEditMode(true);
    setEditExpId(exp.professionalWorkExperienceId);
    setForm({
      jobTitle: exp.jobTitle || "",
      company: exp.company || "",
      companyWebsite: exp.companyWebsite || "",
      employmentType: exp.employmentTypeId || null,
      location: exp.location || "",
      startMonth: exp.startDate ? exp.startDate.split("-")[1] : null,
      startYear: exp.startDate ? parseInt(exp.startDate.split("-")[0]) : null,
      endMonth: exp.endDate ? exp.endDate.split("-")[1] : null,
      endYear: exp.endDate ? parseInt(exp.endDate.split("-")[0]) : null,
      current: !exp.endDate,
      description: exp.jobDetails || "",
      skills: exp.skillsList?.map((skill) => skill.skillId) || [],
    });

    // Set selected skills for autocomplete
    setSelectedSkills(exp.skillsList || []);

    // Clear date errors when editing
    setDateErrors({
      startDate: "",
      endDate: "",
    });
    setSidebarVisible(true);
  };

  const handleDeleteExperience = async (experienceId?: string) => {
    const idToDelete = experienceId || deleteId;
    if (!accessToken || !idToDelete) return;
    try {
      await deleteProfessionalExperience(accessToken, idToDelete);
      setExperiences((prev) =>
        prev.filter((exp) => exp.professionalWorkExperienceId !== idToDelete)
      );
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Experience deleted successfully",
        life: 3000,
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete experience",
        life: 3000,
      });
      console.error("Failed to delete experience", error);
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <>
      <Toast ref={toast} baseZIndex={5000} />
      {(experiences?.length > 0 || isOwnProfile) && (
        <>
          <Card
            className="mb-3 shadow-1"
            title={
              <div className="flex align-items-center justify-content-between mb-4">
                <span className="text-title font-semibold">
                  Professional Experience Outside LBMC
                </span>
                {isOwnProfile && (
                  <Button
                    icon="pi pi-plus"
                    label="Add experience"
                    size="small"
                    severity="info"
                    data-pr-tooltip="Add experience"
                    data-pr-position="right"
                    aria-label="Add Experience"
                    className="p-button p-component"
                    onClick={() => setSidebarVisible(true)}
                  />
                )}
              </div>
            }
          >
            <div className="flex flex-column gap-4">
              {loading ? (
                <div>
                  {["s1", "s2"].map((id) => (
                    <div
                      key={id}
                      className="p-3 flex justify-content-between gap-4 border-round surface-card mb-2"
                    >
                      <div className="flex flex-column">
                        <Skeleton width="220px" height="1.2rem" className="mb-2" />
                        <Skeleton width="160px" height="1rem" className="mb-2" />
                        <Skeleton width="360px" height="1rem" className="mb-2" />
                        <Skeleton width="480px" height="1rem" className="mb-2" />
                        <div className="flex flex-wrap gap-2 mt-2">
                          <Skeleton width="90px" height="1.4rem" className="mr-2" />
                          <Skeleton width="90px" height="1.4rem" className="mr-2" />
                        </div>
                      </div>
                      <div>
                        <Skeleton width="120px" height="1rem" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                experiences.map((exp, index) => (
                  <React.Fragment key={exp.professionalWorkExperienceId}>
                    <div className="flex justify-content-between gap-4">
                      <div className="flex flex-column">
                        <span className="font-semibold text-base mb-2">
                          {exp.jobTitle}
                        </span>
                        <span className="text-sm font-medium mb-2">
                          {exp.company}
                        </span>
                        {exp.companyWebsite ? (
                          <a
                            href={
                              exp.companyWebsite.startsWith("https")
                                ? exp.companyWebsite
                                : `https://${exp.companyWebsite}`
                            }
                            className="text-sm mb-2 text-primary hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {exp.companyWebsite}
                          </a>
                        ) : null}
                        <span className="text-sm profile-description mb-2">
                          {exp.startDate ? formatDateUsingMoment(exp.startDate) : ""}
                          {exp.startDate && exp.endDate
                            ? ` - ${formatDateUsingMoment(exp.endDate)}`
                            : ""}
                          {exp.startDate ? `, ${exp.location}` : exp.location}
                        </span>
                        <span className="text-sm line-height-2 mb-2">
                          {exp.jobDetails}
                        </span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {exp.skillsList.map((skill) => (
                            <span
                              key={skill.skillId}
                              className="px-2 py-1 text-sm font-medium border-round-3xl course-name-font"
                            >
                              {skill.skillName}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex align-items-start gap-1">
                        {isOwnProfile && (
                          <>
                            <Button
                              icon="pi pi-pencil"
                              className="p-button-text p-button shadow-none"
                              onClick={() => handleEditExperience(exp)}
                              tooltip="Edit experience"
                              aria-label="Edit Experience"
                            />
                            <Button
                              icon="pi pi-trash"
                              className="p-button-text p-button-danger shadow-none"
                              tooltip="Delete experience"
                              aria-label="Delete Experience"
                              onClick={() => {
                                confirmDialog({
                                  message:
                                    "Are you sure you want to delete this experience?",
                                  header: "Confirmation",
                                  icon: "pi pi-exclamation-triangle",
                                  defaultFocus: "reject",
                                  accept: () => {
                                    void handleDeleteExperience(
                                      exp.professionalWorkExperienceId
                                    );
                                  },
                                  reject: () => {
                                    toast.current?.show({
                                      severity: "warn",
                                      summary: "Cancelled",
                                      detail: "Delete cancelled",
                                      life: 3000,
                                    });
                                  },
                                });
                              }}
                            />
                          </>
                        )}
                      </div>
                    </div>
                    {index < experiences.length - 1 && <Divider />}
                  </React.Fragment>
                ))
              )}
            </div>
          </Card>
          <Sidebar
            header={customHeader}
            visible={sidebarVisible}
            position="right"
            onHide={() => {
              setSidebarVisible(false);
              setEditMode(false);
              setEditExpId(null);
              setForm({
                jobTitle: "",
                company: "",
                companyWebsite: "",
                employmentType: null,
                location: "",
                startMonth: null,
                startYear: null,
                endMonth: null,
                endYear: null,
                current: false,
                description: "",
                skills: [],
              });
              setSelectedSkills([]);
              setSkillInput("");
              setDateErrors({
                startDate: "",
                endDate: "",
              });
            }}
            style={{ width: "55vw", padding: 0 }}
            showCloseIcon
            blockScroll
            className="p-0"
          >
            <Divider />
            <form className="h-full flex flex-column p-0">
              <div className="pt-3 px-1 flex-1 flex flex-column overflow-y-auto">
                <div className="mb-4">
                  <label
                    htmlFor="jobTitle"
                    className="block mb-2 text-900 text-base"
                  >
                    Job Title <span style={{ color: "red" }}>*</span>
                  </label>
                  <InputText
                    id="jobTitle"
                    value={form.jobTitle}
                    onChange={(e) => handleChange("jobTitle", e.target.value)}
                    placeholder="e.g., Senior Financial Analyst"
                    className="w-full text-sm input-sm-height"
                  />
                </div>
                <div className="mb-4">
                  <label
                    htmlFor="company"
                    className="block mb-2 text-900 text-base"
                  >
                    Company <span style={{ color: "red" }}>*</span>
                  </label>
                  <InputText
                    id="company"
                    value={form.company}
                    onChange={(e) => handleChange("company", e.target.value)}
                    placeholder="e.g., ACME Corportation"
                    className="w-full text-sm input-sm-height"
                  />
                </div>
                <div className="mb-4">
                  <label
                    htmlFor="website"
                    className="block mb-2 text-900 text-base"
                  >
                    Company Website
                  </label>
                  <InputText
                    id="website"
                    value={form.companyWebsite}
                    onChange={(e) => handleChange("companyWebsite", e.target.value)}
                    placeholder="e.g., https://company.com"
                    className={`w-full text-sm input-sm-height ${form.companyWebsite && !isValidURL(form.companyWebsite)
                      ? "p-invalid"
                      : ""
                      }`}
                  />
                  {form.companyWebsite && !isValidURL(form.companyWebsite) && (
                    <small className="p-error">
                      Please enter a valid URL (e.g., https://example.com)
                    </small>
                  )}
                </div>
                <div className="mb-4">
                  <label
                    htmlFor="employmentType"
                    className="block mb-2 text-900 text-base"
                  >
                    Employment Type <span style={{ color: "red" }}>*</span>
                  </label>
                  <Dropdown
                    inputId="employmentType"
                    value={form.employmentType}
                    options={employmentTypes}
                    onChange={(e) => handleChange("employmentType", e.value)}
                    placeholder="Select type"
                    className="w-full text-sm dropdown-placeholder-sm"
                    style={{ height: "34px" }}
                  />
                </div>
                <div className="mb-4">
                  <label
                    htmlFor="location"
                    className="block mb-2 text-900 text-base"
                  >
                    Location <span style={{ color: "red" }}>*</span>
                  </label>
                  <InputText
                    id="location"
                    value={form.location}
                    onChange={(e) => handleChange("location", e.target.value)}
                    placeholder="e.g., Nashville, TN, USA"
                    className="w-full text-sm input-sm-height"
                  />
                </div>
                <div className="flex gap-3 mb-0">
                  <div style={{ flex: 1 }}>
                    <label
                      htmlFor="startMonth"
                      className="block mb-2 text-900 text-base"
                    >
                      Start Month 
                      {/* <span style={{ color: "red" }}>*</span> */}
                    </label>
                    <Dropdown
                      inputId="startMonth"
                      value={form.startMonth}
                      options={months}
                      onChange={(e) => handleChange("startMonth", e.value)}
                      placeholder="Select month"
                      className={`w-full text-sm dropdown-placeholder-sm ${dateErrors.startDate ? "p-invalid" : ""
                        }`}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label
                      htmlFor="startYear"
                      className="block mb-2 text-900 text-base"
                    >
                      Start Year 
                      {/* <span style={{ color: "red" }}>*</span> */}
                    </label>
                    <Dropdown
                      inputId="startYear"
                      value={form.startYear}
                      options={years}
                      onChange={(e) => handleChange("startYear", e.value)}
                      placeholder="Select year"
                      className={`w-full text-sm dropdown-placeholder-sm ${dateErrors.startDate ? "p-invalid" : ""
                        }`}
                    />
                  </div>
                </div>
                {dateErrors.startDate && (
                  <div className="mb-0">
                    <small className="p-error">{dateErrors.startDate}</small>
                  </div>
                )}
                <div className="mb-4 mt-4">
                  <Checkbox
                    inputId="currentRole"
                    checked={form.current}
                    onChange={(e) => handleChange("current", e.checked || false)}
                  />
                  <label htmlFor="currentRole" className="ml-2 text-900 text-base">
                    I am currently working in this role.
                  </label>
                </div>
                <div className="flex gap-3 mb-0">
                  <div style={{ flex: 1 }}>
                    <label
                      htmlFor="endMonth"
                      className="block mb-2 text-900 text-base"
                    >
                      End Month{" "}
                      {/* {!form.current && <span style={{ color: "red" }}>*</span>} */}
                    </label>
                    <Dropdown
                      inputId="endMonth"
                      value={form.endMonth}
                      options={months}
                      onChange={(e) => handleChange("endMonth", e.value)}
                      placeholder="Select month"
                      className={`w-full text-sm dropdown-placeholder-sm ${dateErrors.endDate ? "p-invalid" : ""
                        }`}
                      disabled={form.current}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label
                      htmlFor="endYear"
                      className="block mb-2 text-900 text-base"
                    >
                      End Year{" "}
                      {/* {!form.current && <span style={{ color: "red" }}>*</span>} */}
                    </label>
                    <Dropdown
                      inputId="endYear"
                      value={form.endYear}
                      options={years}
                      onChange={(e) => handleChange("endYear", e.value)}
                      placeholder="Select year"
                      className={`w-full text-sm dropdown-placeholder-sm ${dateErrors.endDate ? "p-invalid" : ""
                        }`}
                      disabled={form.current}
                    />
                  </div>
                </div>
                {dateErrors.endDate && (
                  <div className="mb-0">
                    <small className="p-error">{dateErrors.endDate}</small>
                  </div>
                )}
                <div className="mb-4 mt-4">
                  <label
                    htmlFor="description"
                    className="block mb-2 text-900 text-base"
                  >
                    Description
                  </label>
                  <textarea
                    id="description"
                    className="p-inputtext p-component w-full text-sm"
                    style={{ minHeight: 80, resize: "vertical" }}
                    value={form.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    placeholder="Additional details about your experience..."
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="skills" className="block mb-2 text-900 text-base">
                    Skills
                  </label>
                  <div className="mb-2">
                    <AutoComplete
                      id="skills"
                      value={skillInput}
                      suggestions={filteredSkills.map((skill) => skill.skillName)}
                      completeMethod={searchSkills}
                      onChange={(e) => setSkillInput(e.value)}
                      onSelect={handleSkillSelect}
                      onKeyDown={handleSkillKeyDown}
                      placeholder="Add relevant skills and press Enter"
                      className="w-full text-sm"
                      dropdown
                      forceSelection={false}
                    />
                    <small className="text-500">
                      Press Enter after typing each skill
                    </small>
                  </div>
                  {selectedSkills.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedSkills.map((skill) => (
                        <span
                          key={skill.skillId}
                          className="inline-flex align-items-center bg-primary-100 text-primary-800 px-2 py-1 border-round text-sm"
                        >
                          {skill.skillName}
                          <button
                            type="button"
                            onClick={() => removeSkill(skill)}
                            className="ml-2 text-primary-600 hover:text-primary-800 border-none bg-transparent cursor-pointer"
                            style={{ fontSize: "0.75rem" }}
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-3 align-items-center mb-3">
                  <Button
                    label={editMode ? "Update experience" : "Add experience"}
                    className="p-button p-component p-button-sm"
                    onClick={handleSubmitEducation}
                    icon={editMode ? "pi pi-pencil" : "pi pi-plus"}
                    type="submit"
                  />
                  <Button
                    label="Cancel"
                    className="p-button p-component p-button-outlined p-button-sm"
                    type="button"
                    onClick={() => emptyFormData()}
                  />
                </div>
              </div>
            </form>
          </Sidebar>
        </>
      )}
    </>
  );
};

export default ProfessionalExperienceOutsideLBMC;
